import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  PickupsDTO,
  PickupDetailsDTO,
  DefaultPickupDataDTO
} from "@/model/data.model";
import { GetPickupResults, SearchParams } from "@/model/component.model";
import store from "../vuex/store";

/// Facade to API Pickups service
/// Implements a cache to allow local searching
export class PickupService {
  static getDefaultPickupData(customerId: string) {
    return axios
      .get(store.getters.BASE_API_URL + "/customers/pickup/" + customerId)
      .then(function(res) {
        return res.data;
      });
  }

  static createDefaultPickupData(defaultPickupData: DefaultPickupDataDTO) {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: defaultPickupData,
      url: store.getters.BASE_API_URL + "/customers/pickup"
    };
    return axios
      .request(config)
      .then(res => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static updateDefaultPickupData(
    customerId: string,
    defaultPickupData: DefaultPickupDataDTO
  ) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      data: defaultPickupData,
      url: store.getters.BASE_API_URL + "/customers/pickup/" + customerId
    };
    return axios
      .request(config)
      .then(res => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static getPickups(
    customerMbeId: string,
    storeId: string,
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 25,
      orderField: "pickupDate",
      orderDirection: "desc"
    } as SearchParams
  ): Promise<GetPickupResults> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + `/shipments/pickup/onlineMBE`,
      params: {
        customerId: customerMbeId,
        storeId: storeId,
        search: search.search,
        page: search.page,
        size: search.pageSize,
        sort: search.orderField + "," + search.orderDirection
      }
    };

    return axios.request(config).then((res: AxiosResponse) => {
      const pickups = res.data as PickupsDTO;
      const searchParams = {
        search: search.search,
        orderField: search.orderField,
        orderDirection: search.orderDirection,
        totalElements: res.data.totalElements,
        totalPages: res.data.totalPages,
        numberOfElements: res.data.numberOfElements,
        page: res.data.number,
        pageSize: res.data.size
      } as SearchParams;

      return { pickups, searchParams } as GetPickupResults;
    });
  }

  static deletePickups(
    storeId: string,
    pickups: PickupsDTO,
    username: string,
    language: string
  ): Promise<any> {
    if (pickups.content.length < 1) {
      throw "Pickup list should have at least one element";
    } else {
      const config: AxiosRequestConfig = {
        method: "DELETE",
        url: store.getters.BASE_API_URL + `/shipments/pickup/onlineMBE`,
        params: {
          pickupId: pickups.content[0].pickupId,
          storeId: storeId,
          username: username,
          language: language
        }
      };
      return axios.request(config).then((res: any) => {
        if (pickups.content.length > 1) {
          pickups.content.shift();
          return this.deletePickups(storeId, pickups, username, language);
        } else {
          return res;
        }
      });
    }
  }

  static savePickup(
    pickup: PickupDetailsDTO,
    username: string,
    language: string
  ): Promise<PickupDetailsDTO> {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: pickup,
      params: {
        username: username,
        language: language
      },
      url: store.getters.BASE_API_URL + `/shipments/pickup/onlineMBE`
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  static searchStorePickups(
    customerMbeId: string,
    storeId: string,
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 25,
      orderField: "pickupDate",
      orderDirection: "desc"
    } as SearchParams,
    date: {
      dateStart?: string;
      dateEnd?: string;
    }
  ): Promise<GetPickupResults> {
    let addTime = false;
    if (search.orderField === "pickupDate") {
      addTime = true;
    }
    const config: AxiosRequestConfig = {
      method: "POST",
      url: addTime
        ? store.getters.BASE_API_URL +
          `/shipments/pickup/onlineMBE/search` +
          "?page=" +
          search.page +
          "&size=" +
          search.pageSize +
          "&sort=" +
          search.orderField +
          "," +
          search.orderDirection +
          "&sort=pickupTime," +
          search.orderDirection
        : store.getters.BASE_API_URL +
          `/shipments/pickup/onlineMBE/search` +
          "?page=" +
          search.page +
          "&size=" +
          search.pageSize +
          "&sort=" +
          search.orderField +
          "," +
          search.orderDirection,
      data: {
        customerId: customerMbeId,
        storeId: storeId,
        dateFrom: (date && date.dateStart) || undefined,
        dateTo: (date && date.dateEnd) || undefined,
        username: store.getters["customer/config"].username,
        showPickupsWithSameCustomerId:
          store.getters["customer/config"].showPickupsWithSameCustomerId
      }
    };

    return axios.request(config).then((res: AxiosResponse) => {
      const pickups = res.data as PickupsDTO;
      const searchParams = {
        search: search.search,
        orderField: search.orderField,
        orderDirection: search.orderDirection,
        totalElements: res.data.totalElements,
        totalPages: res.data.totalPages,
        numberOfElements: res.data.numberOfElements,
        page: res.data.number,
        pageSize: res.data.size
      } as SearchParams;

      return { pickups, searchParams } as GetPickupResults;
    });
  }
}
